/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */

import isNonNull from '@openphone/lib/isNonNull'
import { autorun, makeAutoObservable, runInAction } from 'mobx'

import { logError } from '@src/lib/log'
import Collection from '@src/service/collections/Collection'
import PersistedCollection from '@src/service/collections/PersistedCollection'
import type { Invitation } from '@src/service/transport/account'
import { AI_AGENT_ID, getAiAgentConstants } from '@src/shared/aiAgentConstants'

import type Service from '.'
import type { MemberDirectNumber } from './model'
import { MemberModel, PresenceModel } from './model'
import type { MemberRepository } from './worker/repository'

import { getRootLogger } from '@odyssey/logging/logger'

interface UpdateInvite {
  member: MemberModel
  phoneNumberIds?: string[]
  needsNewNumber?: boolean
}

export default class MemberStore {
  collection: PersistedCollection<MemberModel, MemberRepository>
  presence: Collection<PresenceModel>
  orgMember = new Collection<MemberModel>({ bindElements: true })
  private _aiAgentMember: MemberModel

  private _workspaceCreatorId: string | null = null

  private get log() {
    return getRootLogger().getChild(['MemberStore'])
  }

  constructor(private service: Service) {
    this.collection = new PersistedCollection({
      table: service.storage.table('member'),
      classConstructor: () => new MemberModel(service.member),
      compare: (a, b) => a.name.localeCompare(b.name),
    })
    this.presence = new Collection({
      bindElements: true,
    })

    this._aiAgentMember = new MemberModel(this)
    makeAutoObservable(this, {})
    this.subscribeToWebSocket()

    // Reacts to: flags.webSonaToQuoAgentRename
    // Keeps the AI agent member's display name in sync with the rename flag. Without this,
    // the name is set once at construction time and never updates when the flag loads.
    // No disposal needed — this store lives for the lifetime of the app.
    autorun(() => {
      const aiAgentConstants = getAiAgentConstants(
        this.service.flags.flags.webSonaToQuoAgentRename,
      )
      this._aiAgentMember.deserialize({
        id: AI_AGENT_ID,
        firstName: aiAgentConstants.shortName,
        pictureUrl: aiAgentConstants.lightAvatar,
        pictureUrlDark: aiAgentConstants.darkAvatar,
      })
    })
  }

  get(id: string) {
    if (id === AI_AGENT_ID) {
      return this._aiAgentMember
    }

    return this.collection.get(id)
  }

  get directNumbers(): MemberDirectNumber[] {
    return this.collection.list.map((member) => member.directNumber).filter(isNonNull)
  }

  get aiAgentMember() {
    return this.get(this._aiAgentMember.id)
  }

  getWorkspaceCreatorId(): string | null {
    if (this._workspaceCreatorId !== null) {
      return this._workspaceCreatorId
    }

    if (this.collection.list.length === 0) {
      this.log.error(
        'No members found in the workspace while trying to determine the workspace creator.',
      )
      return null
    }

    let firstMember = this.collection.list[0]

    for (const member of this.collection.list) {
      if (member.createdAt === null) {
        continue
      }

      if (member.createdAt < (firstMember?.createdAt ?? Infinity)) {
        firstMember = member
      }
    }

    if (!firstMember) {
      this.log.error(
        'No member found in the workspace while trying to determine the workspace creator.',
      )
      return null
    }

    this._workspaceCreatorId = firstMember.id

    return firstMember.id
  }

  get isShowOnCallStatusEnabled() {
    return this.service.capabilities.features.showOnCallStatusEnabled
  }

  getSharedPhoneNumbers(memberId: string) {
    return this.service.phoneNumber.collection.list.filter((pn) =>
      pn.isSharedWith(memberId),
    )
  }

  /**
   * Set of member IDs that have at least one shared phone number.
   * Computed once and cached - O(PhoneNumbers) instead of O(Members × PhoneNumbers).
   */
  get memberIdsWithPhones(): Set<string> {
    const memberIds = new Set<string>()
    for (const phoneNumber of this.service.phoneNumber.collection.list) {
      for (const user of phoneNumber.users) {
        if (user.id) {
          memberIds.add(user.id)
        }
      }
    }
    return memberIds
  }

  /**
   * Check if a member has any shared phone numbers.
   * O(1) lookup using cached index.
   */
  hasPhones(memberId: string): boolean {
    return this.memberIdsWithPhones.has(memberId)
  }

  fetch() {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.collection.performQuery((repo) => repo.all())
    return this.service.transport.account.members
      .list()
      .then((res) => this.collection.load(res, { deleteOthers: true }))
  }

  setRole(member: MemberModel): Promise<any> {
    this.orgMember.put(member)
    this.collection.put(member)
    if (member.status === 'invited' && this.service.organization.current) {
      return this.service.transport.account.organization
        .for(this.service.organization.current.id)
        .invites.update(member.id, { role: member.role })
    } else {
      return this.service.transport.account.members.put(member.id, { role: member.role })
    }
  }

  fetchAdmin() {
    if (!this.service.organization.current) {
      return this.handleCurrentOrganizationAbscence()
    }

    return this.service.transport.account.organization
      .for(this.service.organization.current.id)
      .member.fetch()
      .then((res) => {
        const members = res.map((json) =>
          new MemberModel(this.service.member).deserialize(json),
        )
        this.orgMember.putBulk(members)
        return members
      })
  }

  fetchPresence() {
    if (!this.service.organization.current) {
      return this.handleCurrentOrganizationAbscence()
    }

    return this.service.transport.account.organization
      .for(this.service.organization.current.id)
      .presence.list()
      .then((res) => {
        const entities = res
          .map((json) => {
            if (!json.userId) {
              return null
            }

            const presence =
              this.presence.get(json.userId) ?? new PresenceModel(this.service.member)

            presence.deserialize(json)

            return presence
          })
          .filter(isNonNull)

        this.presence.putBulk(entities)
        return entities
      })
  }

  updatePresence(presence: PresenceModel) {
    this.presence.put(presence)
    return this.service.transport.account.presence.put(presence.serialize())
  }

  setDoNotDisturb(presence: PresenceModel) {
    if (presence.snoozedUntil && presence.snoozedUntil > Date.now()) {
      const duration = (presence.snoozedUntil - Date.now()) / 1000 / 60
      return this.service.transport.account.presence.doNotDisturb(duration)
    } else {
      return this.service.transport.account.presence.clearDoNotDisturb()
    }
  }

  invite = (invite: Invitation) => {
    if (!this.service.organization.current) {
      return this.handleCurrentOrganizationAbscence()
    }

    return this.service.transport.account.organization
      .for(this.service.organization.current.id)
      .invites.send(invite)
      .then((res) => {
        this.service.analytics.workspace.invited()
        return this.orgMember.put(new MemberModel(this.service.member).deserialize(res))
      })
  }

  reinvite = async (memberId: string) => {
    if (!this.service.organization.current) {
      return this.handleCurrentOrganizationAbscence()
    }

    const invite = await this.service.organization.getInvite(memberId)

    if (!invite) {
      logError(new Error('Failed to reinvite member'))
      throw new Error('Failed to reinvite member')
    }

    await this.service.transport.account.organization
      .for(this.service.organization.current.id)
      .invites.resend(invite.id)

    this.service.analytics.workspace.invited()
  }

  inviteBulk = (invites: Invitation[]) => {
    if (!this.service.organization.current) {
      return this.handleCurrentOrganizationAbscence()
    }

    return this.service.transport.account.organization
      .for(this.service.organization.current.id)
      .invites.sendBulk(invites)
      .then((res) => {
        this.service.analytics.workspace.invited()
        return res
      })
  }

  // TODO: this will append the phone number to the existing invites but it's not an ideal solution
  // https://linear.app/openphone/issue/GROW-1604/improve-the-update-invite-approach
  updateInvite = async ({ member, phoneNumberIds, needsNewNumber }: UpdateInvite) => {
    if (!this.service.organization.current) {
      return this.handleCurrentOrganizationAbscence()
    }

    const { id, role } = member

    return await this.service.transport.account.organization
      .for(this.service.organization.current.id)
      .invites.update(id, {
        role,
        phoneNumberIds,
        needsNewNumber,
      })
  }

  uninvite = (inviteId: string) => {
    if (!this.service.organization.current) {
      return this.handleCurrentOrganizationAbscence()
    }

    this.orgMember.delete(inviteId)
    return this.service.transport.account.organization
      .for(this.service.organization.current.id)
      .invites.delete(inviteId)
  }

  delete = (member: MemberModel) => {
    if (!this.service.organization.current) {
      return this.handleCurrentOrganizationAbscence()
    }

    this.collection.delete(member)
    this.orgMember.delete(member)
    return this.service.transport.account.organization
      .for(this.service.organization.current.id)
      .member.delete(member.id)
  }

  private subscribeToWebSocket() {
    this.service.transport.onNotificationData.subscribe((data) => {
      switch (data.type) {
        case 'user-presence-update': {
          if (!data.presence.userId) {
            return
          }

          const presence =
            this.presence.get(data.presence.userId) ??
            new PresenceModel(this.service.member)
          presence.deserialize(data.presence)

          return this.presence.put(presence)
        }
        case 'member-update': {
          // Check if there's an existing member with 'invited' status that matches the email in the update.
          const invitedMember = this.orgMember.list.find(
            (member) => member.status === 'invited' && member.email === data.member.email,
          )

          // If an invited member exists and the update changes their status to something other than 'invited',
          // we need to remove the invited instance.
          if (invitedMember && data.member.status !== 'invited') {
            this.orgMember.delete(invitedMember.id)
          }

          this.orgMember.put(
            new MemberModel(this.service.member).deserialize(data.member),
          )
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
          this.collection.load(data.member)

          if (data.member.id === this.service.user.current?.id) {
            void this.service.organization.phoneNumber.fetch()
          }
          break
        }
        case 'member-delete': {
          this.orgMember.delete(data.memberId)

          if (this._workspaceCreatorId === data.memberId) {
            runInAction(() => {
              this._workspaceCreatorId = null
            })
          }

          return this.collection.delete(data.memberId)
        }
      }
    })
  }

  private handleCurrentOrganizationAbscence() {
    if (this.service.transport.online) {
      logError(new Error('Current organization is null'))
      return Promise.reject(new Error('An error occurred'))
    } else {
      return Promise.reject(new Error("Can't perform this action while offline"))
    }
  }
}
